// src/scripts/useScripts/useDayLock.js
import { computed, onUnmounted, ref, watch } from "vue";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase";

function asYmd(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (v.toDate) return v.toDate().toISOString().slice(0, 10);
  return "";
}

export function useDayLock(venueIdRef) {
  const tradingDate = ref("");
  const weekEnding = ref("");
  const lockedUntil = ref(null);
  const isOpen = ref(false);

  // Exposed so components don't need a second onSnapshot listener for this field.
  // true  → End Week was pressed on Sunday; Start button shows "Start Week"
  // false → cleared by Start Week; normal Start Day / End Day cycle resumes
  const weekStartPending = ref(false);

  const isSunday = computed(() => {
    if (!tradingDate.value) return false;
    return new Date(`${tradingDate.value}T00:00:00Z`).getUTCDay() === 0;
  });

  const isLocked = computed(() => {
    if (!lockedUntil.value?.toDate) return false;
    return Date.now() < lockedUntil.value.toDate().getTime();
  });

  let unsub = null;

  function stop() {
    if (unsub) {
      unsub();
      unsub = null;
    }
  }

  function start() {
    watch(
      venueIdRef,
      (v) => {
        stop();

        const id = String(v || "").trim();
        if (!id) {
          tradingDate.value = "";
          weekEnding.value = "";
          lockedUntil.value = null;
          isOpen.value = false;
          weekStartPending.value = false;
          return;
        }

        unsub = onSnapshot(
          doc(db, "venues", id, "day_state", "state"),
          (snap) => {
            const data = snap.exists() ? snap.data() || {} : {};
            tradingDate.value = asYmd(data.tradingDate);
            weekEnding.value = asYmd(data.weekEndingDate);
            lockedUntil.value = data.lockedUntil || null;
            isOpen.value = data.isOpen === true;
            weekStartPending.value = data.weekStartPending === true;
          },
          (err) => console.warn("[useDayLock] snapshot error:", err)
        );
      },
      { immediate: true }
    );
  }

  onUnmounted(() => stop());

  return {
    tradingDate,
    weekEnding,
    isOpen,
    weekStartPending,
    isSunday,
    lockedUntil,
    isLocked,
    start,
    stop,
  };
}